import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip, PieChart as RechartsPie, Pie, Cell, Legend 
} from 'recharts';
import {
  Target, Plus, Clock, BarChart3, CheckCircle, DollarSign,
  TrendingUp, Award, AlertCircle, Activity, LineChart,
  PieChart, ChevronRight, Zap
} from 'lucide-react';
import { BoostBadge, isMarketBoosted, boostDaysRemaining } from '../components/shared';
import { extractApiError } from '../utils/helpers';
import ImageUploadField from '../components/shared/ImageUploadField';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts';

const CHART_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

// Default-Liquidity beim Markt-Erstellen. Der User wählt das nicht
// mehr im Form (Plattform stellt die Liquidität), aber das Backend
// braucht den Wert noch zur Initialisierung der Pools (server.py
// create_market_as_creator). Hardcoded als Konstante damit der Wert
// auffindbar ist und nicht in zwei Form-Reset-Stellen einzeln treibt.
const DEFAULT_MARKET_LIQUIDITY = 200;

// "1,0%" -> "1%" (trailing .0 weg), null-safe.
const fmtPct = (r) => (r == null ? null : `${(r * 100).toFixed(1).replace(/\.0$/, '')}%`);

// Vorteile-Block — auf der Pitch-Seite (ausgeloggt) UND über dem
// Bewerbungsformular (eingeloggt, noch kein Creator). Zeigt die echten
// Commission-Sätze, wenn geladen (sonst generischer Text).
const CreatorBenefits = ({ rates }) => {
  const items = [
    {
      icon: DollarSign,
      title: rates?.creator_commission_rate != null
        ? `${fmtPct(rates.creator_commission_rate)} Commission auf jede Wette`
        : 'Commission auf jede Wette',
      desc: 'Du verdienst an jedem Einsatz in den Märkten, die du erstellst.',
    },
    {
      icon: Zap,
      title: 'Boost ins Rampenlicht',
      desc: 'Hebe deine Märkte prominent auf Landing- und Märkte-Seite hervor.',
    },
    {
      icon: BarChart3,
      title: 'Detaillierte Analytics',
      desc: 'Volumen, Performance und Einnahmen je Markt immer im Blick.',
    },
    {
      icon: TrendingUp,
      title: rates?.referral_l1_rate != null
        ? `+ ${fmtPct(rates.referral_l1_rate)} Referral-Provision`
        : 'Zusätzliche Referral-Provision',
      desc: 'Wirb neue Nutzer und verdiene an deren Aktivität zusätzlich mit.',
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/60">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
            <it.icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{it.title}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{it.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const CreatorDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('apply');
  const [myMarkets, setMyMarkets] = useState([]);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commissionHistory, setCommissionHistory] = useState([]);
  const [commissionStats, setCommissionStats] = useState({
    total_creator_commission: 0,
    total_commission_count: 0,
    this_month_commission: 0
  });
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [commissionRates, setCommissionRates] = useState(null);
  const [selectedMarketAnalytics, setSelectedMarketAnalytics] = useState(null);
  const [showMarketAnalyticsDialog, setShowMarketAnalyticsDialog] = useState(false);
  const [creditsBreakdown, setCreditsBreakdown] = useState(null);
  const [availableBoosts, setAvailableBoosts] = useState(0);
  const [boostingId, setBoostingId] = useState(null);
  const navigate = useNavigate();

  // Creator application form
  const [applicationForm, setApplicationForm] = useState({
    application_text: '',
    experience: '',
    portfolio_url: ''
  });

  // Market creation form
  const [marketForm, setMarketForm] = useState({
    title: '',
    description: '',
    category: 'Politics',
    mode: 'prediction',
    civic_category: '',
    resolution_source: '',
    min_liquidity: DEFAULT_MARKET_LIQUIDITY,
    closes_at: '',
    image_url: '',
  });

  useEffect(() => {
    // Commission-Sätze sind öffentlich (für die Pitch-Ansicht, auch
    // ausgeloggt). Creator-/Account-Daten nur wenn eingeloggt.
    api.creator.getCommissionRates().then(r => setCommissionRates(r.data)).catch(() => {});
    if (!user) {
      setLoading(false);
      return;
    }
    fetchCreatorData();
  }, [user]);

  const fetchCreatorData = async () => {
    try {
      if (user.roles?.includes('creator')) {
        const [marketsResponse, commissionsResponse, analyticsResponse] = await Promise.all([
          api.creator.getMarkets(),
          api.profile.getCommissions(),
          api.creator.getAnalyticsOverview().catch(() => ({ data: null }))
        ]);
        
        setMyMarkets(marketsResponse.data);
        setCommissionHistory(commissionsResponse.data);
        if (analyticsResponse.data) {
          // Arrays robust defaulten: ist die Response non-null aber ohne
          // categoryBreakdown/marketPerformance (z.B. Creator ohne
          // Märkte), crashte sonst `.length`/`.map` im Render -> die
          // ganze /creator-Seite ging in die Error-Boundary.
          setAnalyticsOverview({
            ...analyticsResponse.data,
            categoryBreakdown: analyticsResponse.data.categoryBreakdown || [],
            marketPerformance: analyticsResponse.data.marketPerformance || [],
          });
        }
        
        const creatorCommissions = commissionsResponse.data.filter(c => c.type === 'creator');
        const totalCommission = creatorCommissions.reduce((sum, c) => sum + c.amount, 0);
        const thisMonth = new Date();
        const monthStart = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
        const thisMonthCommission = creatorCommissions
          .filter(c => new Date(c.created_at) >= monthStart)
          .reduce((sum, c) => sum + c.amount, 0);
        
        setCommissionStats({
          total_creator_commission: totalCommission,
          total_commission_count: creatorCommissions.length,
          this_month_commission: thisMonthCommission
        });
        
        setActiveTab('dashboard');
        
        // Fetch credits breakdown — Postgres liefert Decimals als
        // Strings, deshalb hier zu Number coercen damit toFixed() läuft.
        try {
          const cbRes = await api.creator.getCreditsBreakdown();
          const d = cbRes.data || {};
          setCreditsBreakdown({
            ...d,
            total_balance: Number(d.total_balance ?? 0),
            from_bets: Number(d.from_bets ?? 0),
            from_commissions: Number(d.from_commissions ?? 0),
            from_referrals: Number(d.from_referrals ?? 0),
          });
        } catch {}

        // Verfuegbare Market-Boosts fuer den "Boosten"-Button.
        try {
          const boostsRes = await api.creator.getBoosts();
          setAvailableBoosts(boostsRes.data?.available_boosts ?? 0);
        } catch {}
      } else {
        try {
          const appsResponse = await api.admin.getCreatorApplications();
          const userApp = appsResponse.data.find(app => app.user_id === user.id);
          setApplication(userApp);
        } catch (err) {
          // User might not have access, that's ok
        }
      }
    } catch (error) {
      console.error('Error fetching creator data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketAnalytics = async (marketId) => {
    try {
      const response = await api.creator.getMarketAnalytics(marketId);
      setSelectedMarketAnalytics(response.data);
      setShowMarketAnalyticsDialog(true);
    } catch (error) {
      toast.error('Failed to load market analytics');
    }
  };

  const handleBoostMarket = async (marketId) => {
    if (boostingId) return;
    setBoostingId(marketId);
    try {
      const res = await api.creator.boostMarket(marketId);
      toast.success(`Markt geboostet für ${res.data?.duration_days ?? 7} Tage!`);
      await fetchCreatorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Boost fehlgeschlagen');
    } finally {
      setBoostingId(null);
    }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    try {
      await api.creator.apply(applicationForm);
      toast.success('Creator application submitted successfully!');
      setApplicationForm({ application_text: '', experience: '', portfolio_url: '' });
      fetchCreatorData();
    } catch (error) {
      toast.error(extractApiError(error, 'Bewerbung konnte nicht gesendet werden'));
    }
  };

  const createMarket = async (e) => {
    e.preventDefault();
    try {
      await api.creator.createMarket({
        ...marketForm,
        closes_at: new Date(marketForm.closes_at).toISOString()
      });
      toast.success('Market created and submitted for approval!');
      setMarketForm({
        title: '',
        description: '',
        category: 'Politics',
        mode: 'prediction',
        civic_category: '',
        resolution_source: '',
        min_liquidity: DEFAULT_MARKET_LIQUIDITY,
        closes_at: '',
        image_url: '',
      });
      fetchCreatorData();
    } catch (error) {
      toast.error(extractApiError(error, 'Markt konnte nicht erstellt werden'));
    }
  };

  // Ausgeloggt: öffentliche „Creator werden"-Pitch mit Vorteilen +
  // Commission-Sätzen und CTA zur Registrierung (statt Redirect/Blank).
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-orange-500 hover:bg-orange-500 text-white">Creator-Programm</Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4 dark:text-gray-100">Werde Creator auf PredFuture</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Erstelle eigene Vorhersage-Märkte und verdiene an jeder Wette mit – kostenlos starten.
            </p>
          </div>
          <CreatorBenefits rates={commissionRates} />
          <div className="text-center mt-10 space-y-3">
            <Button size="lg" className="px-8" onClick={() => navigate('/register')}>
              Jetzt registrieren &amp; Creator werden
            </Button>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Schon dabei?{' '}
              <button type="button" className="text-emerald-600 dark:text-emerald-400 underline" onClick={() => navigate('/login')}>
                Anmelden
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Target className="h-12 w-12 text-emerald-600 mx-auto animate-spin dark:text-emerald-400" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading creator dashboard...</p>
        </div>
      </div>
    );
  }

  // If user is not a creator and doesn't have an application
  if (!user.roles?.includes('creator') && !application) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 dark:text-gray-100">Werde Creator</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Bewirb dich als Market-Creator und baue eigene Vorhersage-Märkte – verdiene an jeder Wette mit.
            </p>
          </div>

          <div className="mb-8">
            <CreatorBenefits rates={commissionRates} />
          </div>

          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-6 w-6" />
                Creator Application
              </CardTitle>
              <CardDescription>
                Tell us about yourself and why you want to create prediction markets
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitApplication} className="space-y-6">
                <div>
                  <Label htmlFor="application_text">Why do you want to become a creator? *</Label>
                  <textarea
                    id="application_text"
                    className="w-full p-3 border rounded-md h-32 resize-none"
                    value={applicationForm.application_text}
                    onChange={(e) => setApplicationForm({...applicationForm, application_text: e.target.value})}
                    placeholder="Describe your motivation and what types of markets you'd like to create..."
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="experience">Relevant Experience</Label>
                  <textarea
                    id="experience"
                    className="w-full p-3 border rounded-md h-24 resize-none"
                    value={applicationForm.experience}
                    onChange={(e) => setApplicationForm({...applicationForm, experience: e.target.value})}
                    placeholder="Any relevant experience in predictions, trading, journalism, or content creation..."
                  />
                </div>

                <div>
                  <Label htmlFor="portfolio_url">Portfolio / Website / Social Channel</Label>
                  <Input
                    id="portfolio_url"
                    type="text"
                    value={applicationForm.portfolio_url}
                    onChange={(e) => setApplicationForm({...applicationForm, portfolio_url: e.target.value})}
                    placeholder="https://… oder @yourhandle (Instagram, TikTok, X, …)"
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                    Optional. URL oder Social-Handle — egal welche Plattform.
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={!applicationForm.application_text}>
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If user has a pending application
  if (application && application.status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 dark:text-gray-100">Creator Application Pending</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Your application is under review by our team
            </p>
          </div>

          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-6 w-6 text-yellow-500" />
                Application Status: Pending
              </CardTitle>
              <CardDescription>
                We will notify you once your application has been reviewed
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Application Text</Label>
                  <div className="p-3 bg-gray-50 rounded-md dark:bg-slate-800/60">
                    {application.application_text}
                  </div>
                </div>
                
                {application.experience && (
                  <div>
                    <Label>Experience</Label>
                    <div className="p-3 bg-gray-50 rounded-md dark:bg-slate-800/60">
                      {application.experience}
                    </div>
                  </div>
                )}

                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Application submitted: {new Date(application.created_at).toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Creator Dashboard (for approved creators)
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 dark:text-gray-100">Creator Dashboard</h1>
          <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400">Manage your prediction markets</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Desktop Tabs */}
          <TabsList className="hidden sm:flex flex-wrap">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="markets">My Markets</TabsTrigger>
            <TabsTrigger value="create">Create Market</TabsTrigger>
          </TabsList>

          {/* Mobile Dropdown */}
          <div className="sm:hidden w-full mb-4">
            <select 
              value={activeTab} 
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full p-3 border border-gray-200 rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none dark:bg-slate-900/80 dark:border-slate-700"
              style={{
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                backgroundSize: '1rem'
              }}
            >
              <option value="dashboard">📊 Dashboard</option>
              <option value="analytics">📈 Analytics</option>
              <option value="markets">🎯 My Markets</option>
              <option value="create">➕ Create Market</option>
            </select>
          </div>

          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Markets</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{myMarkets.length}</div>
                  <p className="text-xs text-muted-foreground">Markets created by you</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Approved Markets</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {myMarkets.filter(m => m.approval_status === 'approved').length}
                  </div>
                  <p className="text-xs text-muted-foreground">Live and trading</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {myMarkets.filter(m => m.approval_status === 'pending_approval').length}
                  </div>
                  <p className="text-xs text-muted-foreground">Awaiting admin review</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Commissions</CardTitle>
                  <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {commissionStats.total_creator_commission.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">Credits earned from markets</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">This Month</CardTitle>
                  <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {commissionStats.this_month_commission.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">Commission this month</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Payouts</CardTitle>
                  <Award className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {commissionStats.total_commission_count}
                  </div>
                  <p className="text-xs text-muted-foreground">Commission payments received</p>
                </CardContent>
              </Card>
            </div>

            {/* Meine Credits-Übersicht */}
            {creditsBreakdown && (
              <Card className="mt-6 border-emerald-200 dark:border-emerald-500/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    Meine Credits-Übersicht
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg text-center dark:bg-slate-800/60">
                      <p className="text-2xl font-bold">{creditsBreakdown.total_balance.toFixed(0)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Gesamt-Credits</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg text-center dark:bg-emerald-500/10">
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">+{creditsBreakdown.from_bets.toFixed(0)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Aus Wetten gewonnen</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center dark:bg-blue-500/10">
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">+{creditsBreakdown.from_commissions.toFixed(0)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Creator Commissions ({creditsBreakdown.commission_count} Wetten)</p>
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-500/10 rounded-lg text-center">
                      <p className="text-lg font-bold text-purple-600 dark:text-purple-400">+{creditsBreakdown.from_referrals.toFixed(0)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Aus Referrals</p>
                    </div>
                  </div>
                  {creditsBreakdown.chart_data && creditsBreakdown.chart_data.length > 0 && (
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={creditsBreakdown.chart_data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Area type="monotone" dataKey="amount" stroke="#3b82f6" fill="#93c5fd" fillOpacity={0.3} />
                        </AreaChart>
                      </ResponsiveContainer>
                      <p className="text-xs text-gray-400 text-center mt-1 dark:text-gray-500">Commission-Einnahmen (letzte 30 Tage)</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Commission History Summary */}
            {commissionHistory.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Recent Commission History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {commissionHistory.slice(0, 5).map((commission) => (
                      <div key={commission.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                        <div>
                          <div className="font-medium">
                            +{commission.amount.toLocaleString()} Credits
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(commission.created_at).toLocaleDateString()} • {commission.type}
                          </div>
                        </div>
                        <Badge variant="default" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300 dark:text-emerald-200">
                          {commission.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analyticsOverview ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{analyticsOverview.summary.totalMarkets}</div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Markets</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{analyticsOverview.summary.activeMarkets}</div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Active</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{analyticsOverview.summary.totalVolume.toLocaleString()}</div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Volume</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{analyticsOverview.summary.totalBets}</div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Bets</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">{analyticsOverview.summary.uniqueBettors}</div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Unique Bettors</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{analyticsOverview.summary.totalCommission.toLocaleString()}</div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Commission Earned</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Volume Trend Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <LineChart className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        Volume Trend (30 Days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analyticsOverview.volumeTrend.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <AreaChart data={analyticsOverview.volumeTrend}>
                            <defs>
                              <linearGradient id="creatorVolumeGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(val) => val.slice(5)} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip 
                              contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                              formatter={(value) => [value.toLocaleString(), 'Volume']}
                            />
                            <Area type="monotone" dataKey="volume" stroke="#10b981" fill="url(#creatorVolumeGradient)" strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">No data yet</div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Category Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <PieChart className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        Volume by Category
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analyticsOverview.categoryBreakdown.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <RechartsPie>
                            <Pie
                              data={analyticsOverview.categoryBreakdown}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={90}
                              paddingAngle={2}
                              dataKey="volume"
                              nameKey="name"
                              label={({ name, percent }) => percent > 0.05 ? `${name}` : ''}
                            >
                              {analyticsOverview.categoryBreakdown.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                              formatter={(value) => [value.toLocaleString() + ' credits', 'Volume']}
                            />
                            <Legend />
                          </RechartsPie>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">No data yet</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Market Performance Comparison */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-orange-600" />
                      Market Performance Comparison
                    </CardTitle>
                    <CardDescription>Click on a market to view detailed analytics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analyticsOverview.marketPerformance.length > 0 ? (
                      <div className="space-y-3">
                        {analyticsOverview.marketPerformance.map((market, index) => (
                          <div 
                            key={market.id} 
                            className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors dark:bg-slate-800/60 dark:hover:bg-slate-800"
                            onClick={() => fetchMarketAnalytics(market.id)}
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold dark:text-emerald-400">
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{market.title}</p>
                              <div className="flex gap-4 text-sm text-gray-500 dark:text-gray-400">
                                <span>{market.bets} bets</span>
                                <span>{market.volume.toLocaleString()} volume</span>
                              </div>
                            </div>
                            <Badge variant={market.status === 'open' ? 'default' : 'secondary'}>
                              {market.status}
                            </Badge>
                            <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No market performance data yet. Create markets to see analytics here.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Loading analytics...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="markets">
            <div className="space-y-6">
              {myMarkets.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No markets created yet</p>
                    <Button className="mt-4" onClick={() => setActiveTab('create')}>
                      Create Your First Market
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {myMarkets.map((market) => (
                    <Card key={market.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{market.title}</CardTitle>
                            <CardDescription>{market.description}</CardDescription>
                          </div>
                          <Badge variant={
                            market.approval_status === 'approved' ? 'default' :
                            market.approval_status === 'pending_approval' ? 'secondary' :
                            'destructive'
                          }>
                            {market.approval_status === 'pending_approval' ? 'Wartet auf Freigabe' :
                             market.approval_status === 'approved' ? 'Freigegeben' :
                             market.approval_status === 'rejected' ? 'Abgelehnt' :
                             market.approval_status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <Label>Category</Label>
                            <p>{market.category}</p>
                          </div>
                          <div>
                            <Label>Closes At</Label>
                            <p>{new Date(market.closes_at).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <Label>Status</Label>
                            <p>{
                              market.approval_status === 'pending_approval' ? 'Wartet auf Freigabe' :
                              market.approval_status === 'rejected' ? 'Abgelehnt' :
                              market.status === 'open' ? 'Offen' :
                              market.status === 'closed' ? 'Geschlossen' :
                              market.status === 'settled' ? 'Abgerechnet' :
                              market.status
                            }</p>
                          </div>
                          <div>
                            <Label>Created</Label>
                            <p>{new Date(market.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        
                        {market.rejection_reason && (
                          <Alert className="mt-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                              <strong>Rejection Reason:</strong> {market.rejection_reason}
                            </AlertDescription>
                          </Alert>
                        )}
                        
                        {market.slug && market.approval_status === 'approved' && (
                          <div className="mt-4">
                            <Label>Deep Link</Label>
                            <div className="flex gap-2 items-center">
                              <code className="bg-gray-100 px-2 py-1 rounded text-sm flex-1 dark:bg-slate-800">
                                predfuture.com/m/{market.slug}
                              </code>
                            </div>
                          </div>
                        )}

                        {/* Boost: aktiver Boost zeigt Restlaufzeit, sonst
                            Einloese-Button (nur offene, approved Markets). */}
                        {market.approval_status === 'approved' && market.status === 'open' && (
                          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
                            {isMarketBoosted(market) ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <BoostBadge />
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  noch {boostDaysRemaining(market)} {boostDaysRemaining(market) === 1 ? 'Tag' : 'Tage'} im Rampenlicht
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 flex-wrap">
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleBoostMarket(market.id)}
                                  disabled={availableBoosts === 0 || boostingId === market.id}
                                  className="bg-orange-500 hover:bg-orange-600 text-white"
                                >
                                  <Zap className="h-4 w-4 mr-1" fill="currentColor" />
                                  {boostingId === market.id ? 'Boosten…' : 'Markt boosten (7 Tage)'}
                                </Button>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {availableBoosts > 0
                                    ? `${availableBoosts} Boost${availableBoosts === 1 ? '' : 's'} verfügbar`
                                    : 'Kein Boost — im Shop kaufen'}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="create">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-6 w-6" />
                  Create New Market
                </CardTitle>
                <CardDescription>
                  Create a prediction market that will be reviewed by admins before going live
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={createMarket} className="space-y-6">
                  <div>
                    <Label htmlFor="title">Market Title *</Label>
                    <Input
                      id="title"
                      value={marketForm.title}
                      onChange={(e) => setMarketForm({...marketForm, title: e.target.value})}
                      placeholder="Will X happen by Y date?"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <textarea
                      id="description"
                      className="w-full p-3 border rounded-md h-32 resize-none"
                      value={marketForm.description}
                      onChange={(e) => setMarketForm({...marketForm, description: e.target.value})}
                      placeholder="Provide context and details about the market..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="mode">Markt-Typ *</Label>
                      <select
                        id="mode"
                        className="w-full p-2 border rounded-md"
                        value={marketForm.mode}
                        onChange={(e) => setMarketForm({...marketForm, mode: e.target.value})}
                        data-testid="creator-market-mode"
                      >
                        <option value="prediction">Prediction</option>
                        <option value="civic">Civic</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <select
                        id="category"
                        className="w-full p-2 border rounded-md"
                        value={marketForm.category}
                        onChange={(e) => setMarketForm({...marketForm, category: e.target.value})}
                      >
                        <option value="Politics">Politics</option>
                        <option value="Sports">Sports</option>
                        <option value="Technology">Technology</option>
                        <option value="Economics">Economics</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {marketForm.mode === 'civic' && (
                      <div>
                        <Label htmlFor="civic_category">Civic-Kategorie *</Label>
                        <select
                          id="civic_category"
                          className="w-full p-2 border rounded-md"
                          value={marketForm.civic_category}
                          onChange={(e) => setMarketForm({...marketForm, civic_category: e.target.value})}
                          required
                        >
                          <option value="">Bitte wählen</option>
                          <option value="Politik">Politik</option>
                          <option value="Wirtschaft">Wirtschaft</option>
                          <option value="Gesellschaft">Gesellschaft</option>
                          <option value="Umwelt">Umwelt</option>
                          <option value="Technologie">Technologie</option>
                        </select>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="closes_at">Closes At *</Label>
                      <Input
                        id="closes_at"
                        type="datetime-local"
                        value={marketForm.closes_at}
                        onChange={(e) => setMarketForm({...marketForm, closes_at: e.target.value})}
                        required
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="resolution_source">Resolution Source *</Label>
                    <Input
                      id="resolution_source"
                      value={marketForm.resolution_source}
                      onChange={(e) => setMarketForm({...marketForm, resolution_source: e.target.value})}
                      placeholder="e.g., Official election results, Reuters, AP News"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="image_url">Cover-Bild (optional)</Label>
                    <ImageUploadField
                      value={marketForm.image_url}
                      onChange={(url) => setMarketForm({...marketForm, image_url: url})}
                    />
                    <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                      Wird oben auf der Markt-Karte angezeigt. Leer lassen für textbasierte Karte.
                    </p>
                  </div>

                  <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded px-3 py-2 dark:bg-slate-800/60 dark:border-slate-800 dark:text-gray-400">
                    Die Plattform stellt die initiale Liquidität (200 VC pro Outcome). Du zahlst keine Credits für die Markt-Erstellung.
                  </p>

                  <Button type="submit" className="w-full">
                    Markt erstellen
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Market Analytics Dialog */}
        <Dialog open={showMarketAnalyticsDialog} onOpenChange={setShowMarketAnalyticsDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                Market Analytics
              </DialogTitle>
              {selectedMarketAnalytics && (
                <DialogDescription>{selectedMarketAnalytics.market.title}</DialogDescription>
              )}
            </DialogHeader>
            
            {selectedMarketAnalytics && (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg dark:bg-blue-500/10">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{selectedMarketAnalytics.metrics.totalBets}</div>
                    <p className="text-sm text-blue-700 dark:text-blue-300">Total Bets</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg dark:bg-emerald-500/10">
                    <div className="text-2xl font-bold text-green-600">{selectedMarketAnalytics.metrics.totalVolume.toLocaleString()}</div>
                    <p className="text-sm text-green-700">Volume</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg dark:bg-purple-500/10">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{selectedMarketAnalytics.metrics.uniqueBettors}</div>
                    <p className="text-sm text-purple-700">Bettors</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg dark:bg-emerald-500/10">
                    <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{selectedMarketAnalytics.metrics.totalCommission.toLocaleString()}</div>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">Commission</p>
                  </div>
                </div>

                {/* Outcome Stats */}
                <div>
                  <h4 className="font-semibold mb-3">Outcome Distribution</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(selectedMarketAnalytics.outcomeStats).map(([label, stats]) => (
                      <div key={label} className={`p-4 rounded-lg ${label === 'YES' ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className={`font-bold ${label === 'YES' ? 'text-green-700' : 'text-red-700'}`}>{label}</span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{stats.probability.toFixed(1)}%</span>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Bets:</span>
                            <span className="font-medium">{stats.bets}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Volume:</span>
                            <span className="font-medium">{stats.volume.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Daily Trend Chart */}
                {selectedMarketAnalytics.dailyTrend.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Betting Trend</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={selectedMarketAnalytics.dailyTrend}>
                        <defs>
                          <linearGradient id="marketTrendGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(val) => val.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(value) => [value.toLocaleString(), 'Volume']} />
                        <Area type="monotone" dataKey="volume" stroke="#3b82f6" fill="url(#marketTrendGradient)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Market Info */}
                <div className="p-4 bg-gray-50 rounded-lg dark:bg-slate-800/60">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <Badge variant="default" className="ml-2">{selectedMarketAnalytics.market.status}</Badge>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Category:</span>
                      <span className="ml-2 font-medium">{selectedMarketAnalytics.market.category}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Created:</span>
                      <span className="ml-2 font-medium">{new Date(selectedMarketAnalytics.market.created_at).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Avg Bet Size:</span>
                      <span className="ml-2 font-medium">{selectedMarketAnalytics.metrics.avgBetSize.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CreatorDashboard;
